import React from 'react';

import { translate } from 'pages/utils/translate';

import { MIN_CAPTURE_AMOUNT } from './constants';
import { PreAuth } from './types';

interface PreAuthSidebarProps {
  preauth: PreAuth;
  totalCapture: number;
  onCapture: () => void;
  onCancel: () => void;
  hasValidationErrors: boolean;
  isTippingEnabled?: boolean;
  isSurchargeEnabled?: boolean;
}

const PreAuthSidebar: React.FC<PreAuthSidebarProps> = ({
  preauth,
  totalCapture,
  onCapture,
  onCancel,
  hasValidationErrors,
  isTippingEnabled = false,
  isSurchargeEnabled = false,
}) => {
  const totalAuthorized = parseFloat(preauth.amount?.amount as any) || 0;

  const getPreauthDisclaimer = (): React.ReactNode => {
    let featureLabel: React.ReactNode = null;
    let actionText: string = '';

    if (isTippingEnabled && isSurchargeEnabled) {
      featureLabel = <strong>{translate('tipping or surcharging')}</strong>;
      actionText = translate(
        'To add either, cancel the preauthorization and complete the payment as a regular sale.'
      );
    } else if (isTippingEnabled) {
      featureLabel = <strong>{translate('tipping')}</strong>;
      actionText = translate(
        'To add a tip, cancel the preauthorization and complete the payment as a regular sale.'
      );
    } else if (isSurchargeEnabled) {
      featureLabel = <strong>{translate('surcharging')}</strong>;
      actionText = translate(
        'To add a surcharge, cancel the preauthorization and complete the payment as a regular sale.'
      );
    }

    if (!featureLabel) return null;

    return (
      <p className="preauth-sidebar-disclaimer" style={{ color: '#7A8B99', fontSize: '12px' }}>
        {translate("Preauthorizations don't support")} {featureLabel}. {actionText}
      </p>
    );
  };

  const getCaptureButtonTitle = () => {
    if (hasValidationErrors) {
      return 'Please fix validation errors before capturing';
    }
    if (totalCapture <= 0) {
      return 'Select items to capture';
    }
    if (totalCapture <= MIN_CAPTURE_AMOUNT) {
      return `Capture amount must be greater than $${MIN_CAPTURE_AMOUNT.toFixed(2)}`;
    }
    return 'Capture the selected amounts';
  };

  return (
    <div className="totals-details">
      <div className="register-payment-block">
        <div className="register-payments-label">
          <div>
            <span>SALE #{preauth.saleRef} TOTALS</span>
          </div>
        </div>

        <table className="totals">
          <tbody>
            <tr>
              <th>Authorized</th>
              <td className="money">${totalAuthorized.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
        <table>
          <tbody>
            <tr className="total">
              <th id="totalName">To Capture</th>
              <td id="toCapture">${totalCapture.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="register-sidebar-buttons">
        <button
          type="button"
          disabled={totalCapture <= MIN_CAPTURE_AMOUNT || hasValidationErrors}
          onClick={onCapture}
          className="cr-button cr-button--primary cr-button--large cr-button--fill"
          style={{
            width: '100%',
            marginBottom: '12px',
            backgroundColor: '#35B858',
            borderColor: '#35B858',
            padding: '11px 18px',
            fontSize: '16px',
            fontWeight: 'bold',
            lineHeight: '19px',
            borderRadius: '3px',
          }}
          title={getCaptureButtonTitle()}
        >
          <span className="cr-button__content">Capture</span>
        </button>

        <button
          type="button"
          onClick={onCancel}
          className="cr-button cr-button--large"
          style={{
            width: '100%',
            marginBottom: '20px',
            padding: '9px 18px',
            fontSize: '14px',
            background: 'linear-gradient(180deg, #FFFFFF 0%, #F2F5F8 100%)',
            border: '1px solid #BCC4CA',
            borderRadius: '3px',
          }}
        >
          <span className="cr-button__content">Cancel preauthorization</span>
        </button>

        {getPreauthDisclaimer()}
      </div>
    </div>
  );
};

export default PreAuthSidebar;
